import { useEffect, useRef, useState } from "react";
import { Box, Container } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme, useMediaQuery } from "@mui/material";

import { aboutSections, landingSection } from "./AboutData";

interface AboutScrollerProps {
  specificDataFrom: "about" | "landing";
  animationOn: boolean;
}

interface SectionData {
  key: string;
  content: React.ReactNode;
  image?: string;
}

export default function AboutScroller({
    specificDataFrom,
    animationOn,
}: AboutScrollerProps) {
    const triggerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const stickyRef = useRef<HTMLDivElement | null>(null);

    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const topValue = isMdUp ? "90px" : "0px";

    const sections: SectionData[] =
        specificDataFrom === "about"
            ? aboutSections
            : specificDataFrom === "landing"
            ? landingSection
            : [];

    const [currentIndex, setCurrentIndex] = useState(() => {
        const saved = localStorage.getItem(`${specificDataFrom}-section-index`);
        return saved ? parseInt(saved) : 0;
    });

    // Edge cases handing
    useEffect(() => {
        if (typeof window !== "undefined" && window.scrollY < 10) {
            setCurrentIndex(0);
            localStorage.setItem(`${specificDataFrom}-section-index`, "0");
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(`${specificDataFrom}-section-index`, currentIndex.toString());
    }, [currentIndex, specificDataFrom]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: "0px",
            threshold: 0.5,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
            const index = Number(entry.target.getAttribute("data-index"));
            if (entry.isIntersecting) {
                setCurrentIndex(index);
            }
            });
        }, observerOptions);

        triggerRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            triggerRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, [sections.length]);
    

    const { content, image } = sections[currentIndex];

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                zIndex: 1,
            }}
        >
            <Box
                ref={stickyRef}
                sx={{
                    position: "sticky",
                    top: 0,
                    height: "100vh",
                    width: "100%",
                    backgroundColor: "#111",
                    color: "#fff",
                    zIndex: 2,
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        height: { xs: "600px", md: "700px" },
                        mt: { xs: 15, md: 0 },
                        overflow: "visible",
                    }}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={sections[currentIndex].key}
                            initial={
                            animationOn
                                ? currentIndex === 1
                                ? { opacity: 0, y: 30 }
                                : { opacity: 0, y: -30 }
                                : { opacity: 1, y: 0 }
                            }
                            animate={{ opacity: 1, y: 0 }}
                            exit={
                            animationOn
                                ? currentIndex === 1
                                ? { opacity: 0, y: -30 }
                                : { opacity: 0, y: 30 }
                                : { opacity: 1, y: 0 }
                            }
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            style={{ height: "100%" }}
                        >
                            <Box
                                sx={{
                                    flex: "1 0 0",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: { sm: "center", md: "flex-end" },
                                    height: "100%",
                                    zIndex: 2,
                                    maxWidth: { xs: "100%", md: "864px" },
                                    mb: { xs: 2, md: 0 },
                                    px: { xs: 2, md: 0 },
                                    position: "relative",
                                    backgroundColor: "transparent",
                                }}
                            >
                               <Box
                                    sx={{
                                        position: 'relative',
                                        maxHeight: { xs: '400px', md: 'none' },
                                        overflowY: { xs: 'auto', md: 'visible' },
                                        WebkitOverflowScrolling: 'touch',
                                        pr: { xs: '8px', md: 0 },
                                       maskImage: 'linear-gradient(to top, transparent 0%, black 10%, black 100%)',
WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 10%, black 100%)',
scrollbarWidth: 'thin',
'&::-webkit-scrollbar': {
    width: '6px',
},

                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                            borderRadius: '3px',
                                        },
                                    }}
                                >
                                    {content}
                                </Box>
                            </Box>
                        </motion.div>
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        {image && (
                            <motion.img
                                key={sections[currentIndex].key}
                                src={image}
                                alt=""
                                initial={{ opacity: 0, scale: 1.02, filter: "blur(4px)" }}
                                animate={{ opacity: 0.12, scale: 1, filter: "blur(0px)" }}
                                exit={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="about-image"
                                style={{
                                    position: "absolute",
                                    top: topValue,
                                    right: isMdUp ? "0px" : undefined,
                                    left: isMdUp ? "auto" : "0",
                                    bottom: 0,
                                    width: isMdUp
                                    ? currentIndex === 0
                                        ? "664px"
                                        : currentIndex === 1
                                        ? "490px"
                                        : "563px"
                                    : "100%",
                                    height: isMdUp ? "700px" : "100%",
                                    objectFit: "cover",
                                    pointerEvents: "none",
                                    userSelect: "none",
                                    zIndex: 1,
                                    borderRadius: 0,
                                    opacity: 0.08,
                                }}
                            />
                        )}
                    </AnimatePresence>
                </Container>
            </Box>

            {sections.map((_, index) => (
                <Box
                    key={index}
                    ref={(el: HTMLDivElement | null) => {triggerRefs.current[index] = el}}
                    data-index={index}
                    sx={{
                        height: index === sections.length - 1 ? "150vh" : "100vh",
                    }}
                />
            ))}
        </Box>
    );
}
